import React, { useState } from 'react';
import Stack from '@mui/material/Stack';
import {
  DeleteIcon,
  InfoIcon,
  LocationsIcon,
  OrganizationsIcon,
  OwnerIcon,
  RestoreIcon,
  TransferIcon,
  UserIcon,
  DownloadIcon,
} from '@common/utils/icons/icons';
import { useTranslation } from 'react-i18next';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  selectIsUserAdminOrHigher,
  selectTwoFactorRest,
  selectUser,
} from '../../store/general/general-slice-selectors';
import UserRole from '../../models/enums/user-roles';
import ChangeOwnerDialog from './ChangeOwnerDialog';
import CustomConfirmAbortDialog from '../misc/CustomConfirmAbortDialog';
import {
  deleteUserById,
  getAllUserDataById,
  patchUserById,
} from '../../store/organization/organization-actions';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import {
  selectOrganizationsData,
  selectOrganizationUsersData,
} from '../../store/organization/organization-slice-selectors';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import { resetUsersRestState } from '../../store/organization/organization-slice';
import CustomSplitColumnCard from '@common/components/misc/CustomSplitColumnCard';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TextInput from '@common/components/Inputs/TextInput';
import { replaceArgsInMessage } from '../../utils/strings';
import SearchBar from '../ui/SearchBar';
import SearchType from '../../models/enums/search';
import SearchResult from '../../models/misc/search-result';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import useValidatedSelectEffect from '../../hooks/useValidatedSelectEffect';
import UserPatchType from '../../models/enums/user-patch-type';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import {
  validateUserEmail,
  validateUserFirstName,
  validateUserLastName,
  validateUserPhoneNumber,
} from '../../services/validation/user-validation';
import { Language } from '../../models/enums/languages';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import SelectInput from '../forms/SelectInput';
import { navigateSphereWithFilters } from '@common/enums/query-params/filters';
import { useNavigate } from 'react-router-dom';
import OrganizationRole from 'models/enums/organization-roles';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { DistanceUnit } from '@common/enums/configuration/units';
import { PasswordIcon, PrivacyIcon } from '@common/utils/icons/icons';
import { useCookieConsent } from '../../hooks/useCookieConsent';
import { ConsentDecision, PRIVACY_POLICY_URL } from '../../utils/cookieConsent';
import { trackEvent } from '../../services/analytics';
import { AnalyticsEvent } from '../../services/analytics-events';
import SwitchInput from 'components/forms/SwitchInput';
import FormHelperText from '@mui/material/FormHelperText';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import TwoFactorSetup from '../admin/TwoFactorSetup';
import ChangePasswordForm from '../admin/ChangePasswordForm';
import { getUserByToken, resetUserTwoFactor } from '../../store/general/general-actions';
import DisableTwoFactorDialog from './DisableTwoFactorDialog';
import { resetTwoFactorRestState } from 'store/general/general-slice';
import getSaveErrorMessages from '@common/functions/general/get-save-error-messages';
import { DialogActions, DialogContent } from '@mui/material';
import CustomLoader from '@common/components/Feedback/CustomLoader';
import DialogWrapperWithClose from '@common/components/General/DialogWrapperWithClose';

const jsonStyle = {
  m: 0,
  p: 2,
  borderRadius: 2,
  backgroundColor: '#f9fafb',
  border: '1px solid #e5e7eb',
  fontSize: 13,
  overflow: 'auto',
  maxHeight: '100%',
};

const UserInfo: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));
  const currentUser = useAppSelector((state) => selectUser(state));
  const organizationUserData = useAppSelector((state) => selectOrganizationUsersData(state));
  const organizationsData = useAppSelector((state) => selectOrganizationsData(state));
  const twoFactorRest = useAppSelector((state) => selectTwoFactorRest(state));
  const user = organizationUserData.current;

  const [isChangeOwnerDialogOpen, setIsChangeOwnerDialogOpen] = useState(false);
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<SearchResult | null>(null);
  const [isTwoFactorSetupDialogOpen, setIsTwoFactorSetupDialogOpen] = useState(false);
  const [isTwoFactorDisableDialogOpen, setIsTwoFactorDisableDialogOpen] = useState(false);
  const [isConfirmResetTwoFactorDialogOpen, setIsConfirmResetTwoFactorDialogOpen] = useState(false);
  const [isViewAllUserDataDialogOpen, setIsViewAllUserDataDialogOpen] = useState(false);
  const { resetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      patchError: organizationUserData.rest.patchError,
      resetPatchHandler: () =>
        dispatch(resetUsersRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
    });

  const existingUserFirstName =
    user?.name != null && user.name.split(' ').length > 1 ? user.name.split(' ')[0] : null;
  const existingUserLastName =
    user?.name != null
      ? user.name.split(' ').splice(1, user.name.split(' ').length).join(' ')
      : null;

  const {
    value: firstName,
    validationResult: firstNameValidationResult,
    valueChangeHandler: firstNameChangeHandler,
  } = useValidatedInputEffect(
    existingUserFirstName ?? '',
    (newValue) => validateUserFirstName(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('firstName'),
    removeServerError,
    resetToggle,
  );

  const {
    value: lastName,
    validationResult: lastNameValidationResult,
    valueChangeHandler: lastNameChangeHandler,
  } = useValidatedInputEffect(
    existingUserLastName ?? '',
    (newValue) => validateUserLastName(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('lastName'),
    removeServerError,
    resetToggle,
  );

  const {
    value: email,
    validationResult: emailValidationResult,
    valueChangeHandler: emailChangeHandler,
  } = useValidatedInputEffect(
    user?.email ?? '',
    (newValue) => validateUserEmail(newValue, true),
    (_) => () => {},
    getServerValidationResultOrNull('email'),
    removeServerError,
    resetToggle,
  );

  const {
    value: phoneNumber,
    validationResult: phoneNumberValidationResult,
    valueChangeHandler: phoneNumberChangeHandler,
  } = useValidatedInputEffect(
    user?.phoneNumber ?? '',
    (newValue) => validateUserPhoneNumber(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('phoneNumber'),
    removeServerError,
    resetToggle,
  );

  const {
    value: role,
    validationResult: roleValidationResult,
    valueChangeHandler: roleChangeHandler,
  } = useValidatedSelectEffect(
    user?.role ?? UserRole.MEMBER,
    (newValue) => ({ isValid: true, error: t('form-help-text-user-role') }),
    (_) => () => {},
    getServerValidationResultOrNull('role'),
    removeServerError,
    resetToggle,
  );

  const {
    value: language,
    validationResult: languageValidationResult,
    valueChangeHandler: languageChangeHandler,
  } = useValidatedSelectEffect(
    user?.language ?? Language.ENGLISH,
    (newValue) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('language'),
    removeServerError,
    resetToggle,
  );

  const {
    value: distanceUnit,
    validationResult: distanceUnitValidationResult,
    valueChangeHandler: distanceUnitChangeHandler,
  } = useValidatedSelectEffect(
    user?.distanceUnit ?? DistanceUnit.METRIC,
    (newValue) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('distanceUnit'),
    removeServerError,
    resetToggle,
  );

  const { consent, accept, decline } = useCookieConsent();

  if (user == null) {
    return null;
  }

  const isOwnProfile = user?.id === currentUser.data.id;

  const openChangeOwnerDialogClickHandler = () => {
    setIsChangeOwnerDialogOpen(true);
  };

  const closeChangeOwnerDialogHandler = () => {
    setIsChangeOwnerDialogOpen(false);
  };

  const openConfirmDeleteDialogClickHandler = () => {
    setIsConfirmDeleteDialogOpen(true);
  };

  const closeConfirmDeleteDialogHandler = () => {
    setIsConfirmDeleteDialogOpen(false);
  };

  const deleteUserHandler = () => {
    dispatch(deleteUserById({ id: user.id, deletingSelf: user.id === currentUser.data.id }));
  };

  const openConfirmResetTwoFactorDialogClickHandler = () => {
    setIsConfirmResetTwoFactorDialogOpen(true);
  };

  const closeConfirmResetTwoFactorDialogHandler = () => {
    setIsConfirmResetTwoFactorDialogOpen(false);
  };

  const resetTwoFactorHandler = () => {
    dispatch(resetUserTwoFactor(user.id));
    setIsConfirmResetTwoFactorDialogOpen(false);
  };

  const closeSuccessPatchSnackbarHandler = () => {
    dispatch(
      resetUsersRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null, patchId: null }),
    );
  };

  const openAllUserData = () => {
    dispatch(getAllUserDataById(user.id));
    setIsViewAllUserDataDialogOpen(true);
  };

  const closeViewAllUserDataDialogHandler = () => {
    setIsViewAllUserDataDialogOpen(false);
  };

  const downloadAllUserDataHandler = () => {
    if (organizationUserData.currentUserAllData) {
      trackEvent(AnalyticsEvent.AccountDataExported, {
        is_own_account: user.id === currentUser.data.id,
      });
      const blob = new Blob([JSON.stringify(organizationUserData.currentUserAllData, null, 2)], {
        type: 'application/json',
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `user-data-${user.id}.json`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    }
    setIsViewAllUserDataDialogOpen(false);
  };

  const patchUserHandler = () => {
    dispatch(
      patchUserById({
        id: user.id,
        data: {
          organizationId: selectedOrganization?.id,
          role: role !== user.role ? (role as UserRole) : undefined,
          firstName,
          lastName,
          email,
          language: language as Language,
          distanceUnit: distanceUnit as DistanceUnit,
          phoneNumber,
        },
        patchType: UserPatchType.GeneralPatch,
      }),
    );
    setSelectedOrganization(null);
  };

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    patchUserHandler();
  };

  const selectOrganizationClickHandler = (result: SearchResult) => {
    setSelectedOrganization(result);
  };

  const closeSearchHandler = () => {
    setSelectedOrganization(null);
  };

  const openUserOrganizationClickHandler = () => {
    navigate(navigateSphereWithFilters(user.organization.id, true));
  };

  const toggleAnalyticsHandler = () => {
    consent === ConsentDecision.GRANTED ? decline() : accept();
  };

  const twoFactorToggleChangeHandler = () => {
    if (currentUser.data.isTwoFactorEnabled) {
      setIsTwoFactorDisableDialogOpen(true);
    } else {
      setIsTwoFactorSetupDialogOpen(true);
    }
  };

  const closeTwoFactorSetupDialogHandler = () => {
    setIsTwoFactorSetupDialogOpen(false);
    dispatch(getUserByToken());
  };

  const closeTwoFactorDisableDialogHandler = () => {
    setIsTwoFactorDisableDialogOpen(false);
  };

  const closeRestReset2faSnackbarHandler = () => {
    dispatch(
      resetTwoFactorRestState({
        resetUserTwoFactorPostStatus: HttpFetchStatus.Idle,
        resetUserTwoFactorPostError: null,
      }),
    );
  };

  const availableRoles: UserRole[] = [UserRole.ADMIN, UserRole.MODERATOR, UserRole.MEMBER];
  if (
    (organizationsData.sub?.find((org) => org.id === selectedOrganization?.id)?.userCount ?? 1) ===
    0
  ) {
    availableRoles.push(UserRole.OWNER);
  }
  if (!availableRoles.includes(user.role)) {
    availableRoles.push(user.role);
  }

  const isUserEditable =
    (isAdmin &&
      (user.role !== UserRole.OWNER ||
        user.organization.id !== currentUser.data.organization.id)) ||
    user.id === currentUser.data.id;

  return (
    <React.Fragment>
      <Stack spacing={2} alignItems="center" p={0.3}>
        <CustomSplitColumnCard
          title={t('title-general-settings')}
          content={[
            {
              left: (
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={1} py={1}>
                    <UserIcon />
                    <Typography>{t('title-user-general-settings')}</Typography>
                  </Stack>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      {t('table-header-organization')}
                    </Typography>
                    <Button
                      onClick={openUserOrganizationClickHandler}
                      startIcon={
                        user.organization.role === OrganizationRole.LOCATION ? (
                          <LocationsIcon color="action" />
                        ) : (
                          <OrganizationsIcon color="action" />
                        )
                      }
                    >
                      {`${user.organization.name} (${t(`organization-role-${user.organization.role}`)})`}
                    </Button>
                  </Box>
                </Stack>
              ),
              right: (
                <React.Fragment>
                  <form onSubmit={formSubmitHandler}>
                    <Stack py={2} spacing={2}>
                      <TextInput
                        onBlur={patchUserHandler}
                        value={email}
                        disabled={!isUserEditable}
                        hideHelpText={!isUserEditable}
                        onChange={emailChangeHandler}
                        validationResult={emailValidationResult}
                        label={t('input-label-user-email')}
                      />
                      <Grid container>
                        <Grid item xs={12} md={6}>
                          <TextInput
                            onBlur={patchUserHandler}
                            value={firstName}
                            disabled={!isUserEditable}
                            hideHelpText={!isUserEditable}
                            onChange={firstNameChangeHandler}
                            validationResult={firstNameValidationResult}
                            label={t('input-label-user-first-name')}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextInput
                            onBlur={patchUserHandler}
                            value={lastName}
                            disabled={!isUserEditable}
                            hideHelpText={!isUserEditable}
                            onChange={lastNameChangeHandler}
                            validationResult={lastNameValidationResult}
                            label={t('input-label-user-last-name')}
                          />
                        </Grid>
                      </Grid>
                      <TextInput
                        onBlur={patchUserHandler}
                        value={phoneNumber}
                        disabled={!isUserEditable}
                        hideHelpText={!isUserEditable}
                        onChange={phoneNumberChangeHandler}
                        validationResult={phoneNumberValidationResult}
                        label={t('input-label-user-phone-number')}
                      />
                      <SelectInput
                        label={t('input-label-user-role')}
                        value={role}
                        onBlur={patchUserHandler}
                        disabled={!isUserEditable || user.role === UserRole.OWNER}
                        hideHelpText={!isUserEditable || user.role === UserRole.OWNER}
                        onChange={roleChangeHandler}
                        validationResult={roleValidationResult}
                      >
                        {availableRoles.map((r) => (
                          <MenuItem key={r} value={r}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              width="100%"
                              alignItems="center"
                            >
                              {t('user-role-' + r)}
                              <Stack spacing={2} alignItems="center" direction="row">
                                {isUserEditable && user.role === UserRole.OWNER && (
                                  <TooltipIconButton
                                    tooltip={t('info-tooltip-change-owner')}
                                    color="primary"
                                    onClick={openChangeOwnerDialogClickHandler}
                                    icon={<OwnerIcon data-testid="change-owner-button" />}
                                  />
                                )}
                                <Tooltip title={t('user-role-description-' + r)} placement="right">
                                  <InfoIcon color="disabled" />
                                </Tooltip>
                              </Stack>
                            </Stack>
                          </MenuItem>
                        ))}
                      </SelectInput>
                      <SelectInput
                        label={t('input-label-file-language')}
                        value={language}
                        disabled={!isUserEditable}
                        hideHelpText={!isUserEditable}
                        onBlur={patchUserHandler}
                        onChange={languageChangeHandler}
                        validationResult={languageValidationResult}
                      >
                        {Object.values(Language).map((c) => (
                          <MenuItem key={c} value={c}>
                            {t('language-' + c)}
                          </MenuItem>
                        ))}
                      </SelectInput>
                      <SelectInput
                        label={t('input-label-distance-unit')}
                        value={distanceUnit}
                        disabled={!isUserEditable}
                        hideHelpText={!isUserEditable}
                        onBlur={patchUserHandler}
                        onChange={distanceUnitChangeHandler}
                        validationResult={distanceUnitValidationResult}
                      >
                        {Object.values(DistanceUnit).map((c) => (
                          <MenuItem key={c} value={c}>
                            {t('distance-unit-' + c)}
                          </MenuItem>
                        ))}
                      </SelectInput>
                    </Stack>
                    <Button sx={{ display: 'none' }} type="submit" />
                  </form>
                  <Stack spacing={2}>
                    {isAdmin && !isOwnProfile && user.isTwoFactorConfigured && (
                      <Button
                        color="primary"
                        variant="outlined"
                        fullWidth
                        size="large"
                        data-testid="reset-two-factor-button"
                        startIcon={<RestoreIcon data-testid="reset-two-factor-icon" />}
                        onClick={openConfirmResetTwoFactorDialogClickHandler}
                      >
                        {t('button-reset-two-factor')}
                      </Button>
                    )}
                    {isUserEditable && (
                      <Tooltip
                        title={
                          user.role === UserRole.OWNER
                            ? t('info-tooltip-cannot-delete-owner')
                            : undefined
                        }
                      >
                        <span>
                          <Button
                            color="error"
                            variant="outlined"
                            fullWidth
                            size="large"
                            data-testid="delete-button"
                            disabled={
                              user.role === UserRole.OWNER ||
                              organizationUserData.rest.deleteStatus === HttpFetchStatus.Loading
                            }
                            startIcon={<DeleteIcon data-testid="delete-user-button" />}
                            onClick={openConfirmDeleteDialogClickHandler}
                          >
                            {t('button-delete')}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </React.Fragment>
              ),
            },
            isAdmin && isUserEditable
              ? {
                  left: (
                    <Stack spacing={2} pr={2}>
                      <Stack direction="row" alignItems="center" spacing={1} py={1}>
                        <TransferIcon />
                        <Typography>{t('title-transfer-user')}</Typography>
                      </Stack>
                      <CustomAlert
                        severity="info"
                        header={replaceArgsInMessage(t('info-transfer-user-from'), [
                          user.organization.name ?? '',
                        ])}
                      />
                      <Typography>{t('info-transfer-user')}</Typography>
                    </Stack>
                  ),
                  right: (
                    <Stack py={2}>
                      {user.role === UserRole.OWNER ? (
                        <CustomAlert severity="warning" header={t('info-cannot-transfer-owner')} />
                      ) : (
                        <SearchBar
                          hideTitle
                          topLabel={t('input-label-transfer-to')}
                          suggestions={organizationsData.locationSet
                            ?.filter((org) => org.id !== user.organization.id)
                            .map((org) => ({
                              id: org.id,
                              name: org.name,
                              type: SearchType.ORGANIZATION,
                              role: org.role,
                            }))}
                          onItemClick={selectOrganizationClickHandler}
                          initialValue={selectedOrganization?.name}
                          onClose={closeSearchHandler}
                          fields={[SearchType.ORGANIZATION]}
                          fullSize
                          inline
                        />
                      )}
                    </Stack>
                  ),
                }
              : null,
            isOwnProfile
              ? {
                  left: (
                    <Stack spacing={2} pr={2}>
                      <Stack direction="row" alignItems="center" spacing={1} py={1}>
                        <PrivacyIcon />
                        <Typography>{t('title-privacy')}</Typography>
                      </Stack>
                      <CustomAlert
                        severity="info"
                        header={t('info-privacy-local-device')}
                        textMessages={[t('info-privacy-tracking-scope')]}
                      />
                    </Stack>
                  ),
                  right: (
                    <Stack justifyContent="center" spacing={2}>
                      <Stack justifyContent="center">
                        <SwitchInput
                          label={t('label-analytics-tracking')}
                          isChecked={consent === ConsentDecision.GRANTED}
                          onChange={toggleAnalyticsHandler}
                        />
                        <FormHelperText>
                          {t('info-for-more-info-check-privacy-policy')}{' '}
                          <Link href={PRIVACY_POLICY_URL} target="_blank" rel="noopener">
                            {t('link-privacy-policy')}
                          </Link>
                        </FormHelperText>
                      </Stack>
                      <span>
                        <Button
                          color="primary"
                          variant="text"
                          data-testid="request-user-data-button"
                          disabled={
                            organizationUserData.rest.getAllUserDataByIdStatus ===
                            HttpFetchStatus.Loading
                          }
                          startIcon={<DownloadIcon data-testid="request-user-data-button" />}
                          onClick={openAllUserData}
                        >
                          {t('button-get-user-data')}
                        </Button>
                      </span>
                    </Stack>
                  ),
                }
              : null,
            isOwnProfile
              ? {
                  left: (
                    <Stack spacing={2} pr={2}>
                      <Stack direction="row" alignItems="center" spacing={1} py={1}>
                        <PasswordIcon />
                        <Typography>{t('title-security')}</Typography>
                      </Stack>
                    </Stack>
                  ),
                  right: (
                    <Stack spacing={2} justifyContent="center">
                      {currentUser.data.organization.require2fa ? (
                        <Tooltip title={t('tooltip-two-factor-required-by-organization')}>
                          <span>
                            <FormControlLabel
                              control={<Switch checked disabled />}
                              label={t('input-label-two-factor-authentication')}
                            />
                          </span>
                        </Tooltip>
                      ) : currentUser.data.isTwoFactorEnabled &&
                        !currentUser.data.isTwoFactorConfigured ? (
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography color="warning.main">
                            {t('warning-two-factor-not-configured')}
                          </Typography>
                          <Button
                            variant="outlined"
                            onClick={() => setIsTwoFactorSetupDialogOpen(true)}
                          >
                            {t('button-configure')}
                          </Button>
                        </Stack>
                      ) : (
                        <SwitchInput
                          label={t('input-label-two-factor-authentication')}
                          onChange={twoFactorToggleChangeHandler}
                          isChecked={currentUser.data.isTwoFactorEnabled}
                        />
                      )}
                      <ChangePasswordForm />
                    </Stack>
                  ),
                }
              : null,
          ]}
        />
      </Stack>
      <ChangeOwnerDialog
        user={user}
        open={isChangeOwnerDialogOpen}
        onClose={closeChangeOwnerDialogHandler}
      />
      <CustomConfirmAbortDialog
        open={isConfirmDeleteDialogOpen}
        title={t('title-delete-user')}
        text={<CustomAlert severity="warning" header={t('warning-deleting-is-permanent')} />}
        confirmButton={t('button-delete')}
        abortButton={t('button-cancel')}
        onAbort={closeConfirmDeleteDialogHandler}
        onConfirm={deleteUserHandler}
      />
      <DialogWrapperWithClose
        title={t('title-user-data')}
        open={isViewAllUserDataDialogOpen}
        onClose={closeViewAllUserDataDialogHandler}
        maxWidth={false} // let the Paper props handle the max width
        TransitionProps={{
          onExited: () => {
            dispatch(
              resetUsersRestState({
                getAllUserDataByIdError: null,
                getAllUserDataByIdStatus: HttpFetchStatus.Idle,
              }),
            );
          },
        }}
        PaperProps={{
          sx: {
            width: {
              // responsive width to look good on small screens, too
              xs: '95vw',
              sm: '80vw',
              md: '60vw',
              lg: '40vw',
            },
            height: {
              xs: '95vh',
              md: '80vh',
            },
          },
        }}
      >
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {organizationUserData.rest.getAllUserDataByIdStatus === HttpFetchStatus.Loading && (
            <Box py={2} height="100%">
              <CustomLoader loadingMessage={t('info-fetching-user-data')} />
            </Box>
          )}
          {organizationUserData.rest.getAllUserDataByIdStatus === HttpFetchStatus.Success && (
            <Box component="pre" sx={jsonStyle}>
              {JSON.stringify(organizationUserData.currentUserAllData, null, 2)}
            </Box>
          )}
          {organizationUserData.rest.getAllUserDataByIdStatus === HttpFetchStatus.Failed && (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CustomAlert
                severity="error"
                header={t('info-loading-user-data-failed')}
                textMessages={getSaveErrorMessages(
                  organizationUserData.rest.getAllUserDataByIdError,
                )}
              />
            </Box>
          )}
          <DialogActions>
            <Button
              disabled={
                organizationUserData.rest.getAllUserDataByIdStatus !== HttpFetchStatus.Success
              }
              variant="contained"
              onClick={downloadAllUserDataHandler}
            >
              {t('button-download')}
            </Button>
          </DialogActions>
        </DialogContent>
      </DialogWrapperWithClose>
      <CustomConfirmAbortDialog
        open={isConfirmResetTwoFactorDialogOpen}
        title={t('title-reset-two-factor')}
        text={<CustomAlert severity="warning" header={t('warning-reset-two-factor')} />}
        confirmButton={t('button-reset')}
        abortButton={t('button-cancel')}
        onAbort={closeConfirmResetTwoFactorDialogHandler}
        onConfirm={resetTwoFactorHandler}
      />
      <CustomConfirmAbortDialog
        open={selectedOrganization != null}
        title={t('title-transfer-user')}
        text={
          <Stack spacing={2}>
            <CustomAlert
              severity="info"
              header={replaceArgsInMessage(t('info-confirm-transfer-user-popup-header'), [
                user.name,
                selectedOrganization?.name ?? '',
              ])}
            />
            <Typography variant="body2">{t('info-transfer-user')}</Typography>
          </Stack>
        }
        confirmButton={t('button-confirm')}
        abortButton={t('button-cancel')}
        onAbort={closeSearchHandler}
        onConfirm={patchUserHandler}
      />
      <CustomSnackbar
        open={organizationUserData.rest.patchStatus === HttpFetchStatus.Success}
        title={t('success-saving-user')}
        onClose={closeSuccessPatchSnackbarHandler}
      />
      <CustomSnackbar
        open={twoFactorRest.resetUserTwoFactorPostStatus === HttpFetchStatus.Success}
        title={t('success-resetting-user-2fa', { user: user.name })}
        onClose={closeRestReset2faSnackbarHandler}
      />
      <CustomSnackbar
        open={twoFactorRest.resetUserTwoFactorPostStatus === HttpFetchStatus.Failed}
        title={t('error-resetting-user-2fa', { user: user.name })}
        textMessages={getSaveErrorMessages(twoFactorRest.resetUserTwoFactorPostError)}
        onClose={closeRestReset2faSnackbarHandler}
      />
      {isTwoFactorSetupDialogOpen && (
        <TwoFactorSetup
          variant="dialog"
          onComplete={closeTwoFactorSetupDialogHandler}
          onCancel={closeTwoFactorSetupDialogHandler}
        />
      )}
      <DisableTwoFactorDialog
        open={isTwoFactorDisableDialogOpen}
        onClose={closeTwoFactorDisableDialogHandler}
      />
    </React.Fragment>
  );
};

export default UserInfo;
